import { useCart } from "@/App";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, updateCartItem, removeFromCart } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-lg bg-white" data-testid="cart-drawer">
        <SheetHeader className="border-b border-stone-200 pb-4">
          <SheetTitle className="flex items-center gap-3 text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
            <ShoppingBag className="w-6 h-6" />
            Votre Panier
            {cart.items?.length > 0 && (
              <span className="text-sm font-normal text-stone-500">
                ({cart.items.reduce((sum, item) => sum + item.quantity, 0)} articles)
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {cart.items?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="empty-cart">
            <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center mb-6">
              <ShoppingBag className="w-12 h-12 text-stone-300" />
            </div>
            <h3 className="text-xl font-semibold text-[#1D3557] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
              Votre panier est vide
            </h3>
            <p className="text-stone-500 mb-6">
              Découvrez nos produits personnalisables
            </p>
            <Button
              onClick={() => setCartOpen(false)}
              asChild
              className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-8"
            >
              <Link to="/boutique">Voir la boutique</Link>
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[calc(100vh-280px)] py-4">
              <div className="space-y-4 pr-4">
                {cart.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 bg-stone-50 rounded-xl"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#1D3557] truncate">{item.product_name}</h4>
                      {item.customization && (
                        <p className="text-sm text-stone-500 truncate">
                          Personnalisation: {item.customization}
                        </p>
                      )}
                      <p className="text-[#C65D47] font-semibold mt-1">
                        {formatPrice(item.price)}
                      </p>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartItem(item.id, item.quantity - 1)}
                            className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-[#C65D47] transition-colors"
                            data-testid={`decrease-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateCartItem(item.id, item.quantity + 1)}
                            className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-[#C65D47] transition-colors"
                            data-testid={`increase-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-stone-400 hover:text-[#E63946] transition-colors"
                          data-testid={`remove-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Cart Footer */}
            <div className="border-t border-stone-200 pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Sous-total</span>
                <span className="text-xl font-bold text-[#1D3557]" data-testid="cart-total">
                  {formatPrice(cart.total)}
                </span>
              </div>
              <p className="text-sm text-stone-500">
                Frais de livraison calculés à la prochaine étape
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setCartOpen(false)}
                  asChild
                  className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-12 text-base font-semibold"
                >
                  <Link to="/checkout" data-testid="checkout-button">
                    Passer commande
                  </Link>
                </Button>
                <Button
                  onClick={() => setCartOpen(false)}
                  variant="outline"
                  asChild
                  className="w-full border-[#1D3557] text-[#1D3557] hover:bg-[#1D3557] hover:text-white rounded-full h-12"
                >
                  <Link to="/boutique">Continuer mes achats</Link>
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
